import { createContext, useContext, useState, useEffect } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/config/firebase'

export interface CompanyConfig {
  name: string
  shortName: string
  logoUrl: string | null
  logoData: string | null
  pdfLogoUrl: string | null
  pdfLogoData: string | null
  watermarkUrl: string | null
  loginLogoUrl: string | null
  faviconUrl: string | null
  siteName: string | null
}

const DEFAULTS: CompanyConfig = {
  name: 'NIBPR Analytics',
  shortName: 'NIBPR',
  logoUrl: null,
  logoData: null,
  pdfLogoUrl: null,
  pdfLogoData: null,
  watermarkUrl: null,
  loginLogoUrl: null,
  faviconUrl: null,
  siteName: null,
}

const CompanyContext = createContext<CompanyConfig>(DEFAULTS)

/** Updates the browser tab title and favicon to match the company config. */
function applyBrowserBranding(config: CompanyConfig) {
  if (typeof document === 'undefined') return

  if (config.siteName && config.siteName.trim()) {
    document.title = config.siteName.trim()
  }

  if (config.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = config.faviconUrl
  }
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<CompanyConfig>(DEFAULTS)

  useEffect(() => {
    const ref = doc(db, 'config', 'company')
    const unsub = onSnapshot(ref, snap => {
      if (snap.exists()) {
        const d = snap.data()
        const next: CompanyConfig = {
          name: d.name ?? DEFAULTS.name,
          shortName: d.shortName ?? DEFAULTS.shortName,
          logoUrl: d.logoUrl ?? null,
          logoData: d.logoData ?? null,
          pdfLogoUrl: d.pdfLogoUrl ?? null,
          pdfLogoData: d.pdfLogoData ?? null,
          watermarkUrl: d.watermarkUrl ?? null,
          loginLogoUrl: d.loginLogoUrl ?? null,
          faviconUrl: d.faviconUrl ?? null,
          siteName: d.siteName ?? null,
        }
        setConfig(next)
        applyBrowserBranding(next)
      }
    }, () => {})
    return unsub
  }, [])

  return <CompanyContext.Provider value={config}>{children}</CompanyContext.Provider>
}

export const useCompany = () => useContext(CompanyContext)
